/**
 * VistoeraTASK-00589 — Personal settings tab.
 * VistoeraTASK-00592 — section is URL-driven (`?section=profile|security|preferences`)
 * so account-menu「个人资料」can deep-link without a second click.
 * Nickname + email (readonly) use patchMyProfile; no parallel edit modal.
 */
import { Button, Input, Menu, message } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import LanguageSwitcher from '../../../components/LanguageSwitcher'
import { useAuth } from '../../AuthContext'
import { patchMyProfile, type SaasApiError } from '../../api'
import { patchCurrentAccountDisplayName } from '../../accountSwitch/deviceAccountsStore'
import { DISPLAY_NAME_MAX } from '../../accountMenu/profileDisplayName'
import ThemeToggle from '../../ui/ThemeToggle'
import {
  parseOverviewSettingsSection,
  type OverviewSettingsSection,
} from './overviewTabKeys'

export default function SettingsTab({ onProfileSaved }: { onProfileSaved?: () => void }) {
  const { t } = useTranslation(['overview', 'accountEntry'])
  const { me, applyMe } = useAuth()
  const user = me?.user
  const [searchParams, setSearchParams] = useSearchParams()
  const section = parseOverviewSettingsSection(searchParams.get('section'))
  const [displayName, setDisplayName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDisplayName((user?.display_name || '').trim())
  }, [user?.display_name])

  const setSection = (key: OverviewSettingsSection) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'settings')
    next.set('section', key)
    setSearchParams(next, { replace: true })
  }

  const trimmed = displayName.trim()
  const canSave =
    !saving && trimmed.length > 0 && trimmed.length <= DISPLAY_NAME_MAX && trimmed !== (user?.display_name || '').trim()

  const onSave = async () => {
    if (!canSave || !user) return
    setSaving(true)
    try {
      const { data } = await patchMyProfile({ display_name: trimmed })
      applyMe(data)
      patchCurrentAccountDisplayName(data.user.id, data.user.display_name)
      message.success(t('accountEntry:profile.saveSuccess'))
      onProfileSaved?.()
    } catch (err) {
      message.error((err as SaasApiError).message || t('accountEntry:profile.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tc-ov-v3-settings" data-testid="overview-settings-tab">
      <Menu
        mode="inline"
        selectedKeys={[section]}
        className="tc-ov-v3-settings-nav"
        onClick={({ key }) => setSection(key as OverviewSettingsSection)}
        items={[
          { key: 'profile', label: t('overview:tabs.v3.settings.profile') },
          { key: 'security', label: t('overview:tabs.v3.settings.security') },
          { key: 'preferences', label: t('overview:tabs.v3.settings.preferences') },
        ]}
      />
      <div className="tc-data-card tc-ov-v3-card tc-ov-v3-settings-body">
        {section === 'profile' ? (
          <div className="tc-ov-v3-settings-form">
            <div>
              <label className="tc-ov-v3-label" htmlFor="ov-settings-nickname">
                {t('accountEntry:profile.displayNameLabel')}
              </label>
              <Input
                id="ov-settings-nickname"
                value={displayName}
                maxLength={DISPLAY_NAME_MAX}
                onChange={(e) => setDisplayName(e.target.value)}
                data-testid="overview-settings-nickname"
              />
              <div className="tc-ov-v3-hint">
                {t('accountEntry:profile.displayNameHint')} · {trimmed.length}/{DISPLAY_NAME_MAX}
              </div>
            </div>
            <div>
              <label className="tc-ov-v3-label" htmlFor="ov-settings-email">
                {t('accountEntry:profile.emailLabel')}
              </label>
              <Input id="ov-settings-email" value={user?.email || ''} disabled readOnly data-testid="overview-settings-email" />
              <div className="tc-ov-v3-hint">{t('accountEntry:profile.emailHint')}</div>
            </div>
            <Button type="primary" loading={saving} disabled={!canSave} onClick={() => void onSave()}>
              {t('accountEntry:profile.save')}
            </Button>
          </div>
        ) : null}
        {section === 'security' ? (
          <div className="tc-ov-v3-hint">{t('overview:tabs.v3.settings.securityHint')}</div>
        ) : null}
        {section === 'preferences' ? (
          <div className="tc-ov-v3-settings-form">
            <div className="gae-row" role="presentation">
              <span className="gae-row-label">{t('accountEntry:menu.uiLanguage')}</span>
              <LanguageSwitcher />
            </div>
            <div className="gae-row" role="presentation">
              <span className="gae-row-label">{t('accountEntry:menu.appearance')}</span>
              <ThemeToggle />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
